/**
 * Monats- und Voranmeldungszeitraum-Arithmetik des Umsatzsteuer-Rechenkerns.
 *
 * Alles lokal (getFullYear/getMonth/getDate) – der Leistungsmonat einer Sollstellung ist
 * ein Kalendermonat in Deutschland, nie ein UTC-Monat.
 *
 * R1 Leistungsmonat: sollMonth → Text-Token „MM/yyyy" → Monat von booking.date
 * (Tag ≥ 29 wird markiert, der Aufrufer erzeugt daraus INFO LEISTUNGSMONAT_AUS_DATUM).
 * Betriebskosten-Sollstellungen (Nachzahlung/Guthaben) gehören in den Abrechnungsmonat,
 * also immer in den Monat von booking.date.
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
import {BookingLite, VatEngineRange, VatPeriodType} from './types';

const MONTH_NAMES_DE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

export const pad2 = (n: number): string => String(n).padStart(2, '0');

/** Tagesdatum als „TT.MM.JJJJ“ – bewusst ohne toLocaleDateString, damit Hinweistexte in Browser, Karma und Node identisch sind. */
export const formatDayDE = (date: Date): string => `${pad2(date.getDate())}.${pad2(date.getMonth() + 1)}.${date.getFullYear()}`;

export const MONTH_KEY_PATTERN = /^(\d{4})-(0[1-9]|1[0-2])$/;

/** „Miete 03/2026", „Warmmiete 04/2026 Müller" → Monat/Jahr. */
export const TEXT_MONTH_TOKEN = /\b(0[1-9]|1[0-2])\/((?:19|20)\d{2})\b/;

export const monthKey = (d: Date): string => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;

export const parseMonthKey = (key: string): { year: number; month: number } | null => {
    const match = MONTH_KEY_PATTERN.exec(key || '');
    return match ? {year: Number(match[1]), month: Number(match[2])} : null;
};

const partsOrThrow = (key: string): { year: number; month: number } => {
    const parts = parseMonthKey(key);
    if (!parts) {
        throw new Error(`Ungültiger monthKey: ${key}`);
    }
    return parts;
};

export const monthStart = (key: string): Date => {
    const {year, month} = partsOrThrow(key);
    return new Date(year, month - 1, 1, 0, 0, 0, 0);
};

export const monthEnd = (key: string): Date => {
    const {year, month} = partsOrThrow(key);
    return new Date(year, month, 0, 23, 59, 59, 999);
};

export const daysInMonth = (year: number, month: number): number => new Date(year, month, 0).getDate();

export const monthLabel = (key: string): string => {
    const {year, month} = partsOrThrow(key);
    return `${MONTH_NAMES_DE[month - 1]} ${year}`;
};

export const addMonths = (key: string, delta: number): string => {
    const {year, month} = partsOrThrow(key);
    return monthKey(new Date(year, month - 1 + delta, 1));
};

export const compareMonthKey = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

export const periodKeyForMonth = (month: number, periodType: VatPeriodType): string =>
    periodType === 'MONAT' ? `M${pad2(month)}` : `Q${Math.ceil(month / 3)}`;

export const parsePeriodKey = (periodKey: string): { type: VatPeriodType; index: number } | null => {
    const monthMatch = /^M(0[1-9]|1[0-2])$/.exec(periodKey || '');
    if (monthMatch) {
        return {type: 'MONAT', index: Number(monthMatch[1])};
    }
    const quarterMatch = /^Q([1-4])$/.exec(periodKey || '');
    if (quarterMatch) {
        return {type: 'QUARTAL', index: Number(quarterMatch[1])};
    }
    return null;
};

export const monthKeysOfPeriod = (year: number, periodKey: string): string[] => {
    const parsed = parsePeriodKey(periodKey);
    if (!parsed) {
        throw new Error(`Ungültiger periodKey: ${periodKey}`);
    }
    if (parsed.type === 'MONAT') {
        return [`${year}-${pad2(parsed.index)}`];
    }
    const firstMonth = (parsed.index - 1) * 3 + 1;
    return [0, 1, 2].map(offset => `${year}-${pad2(firstMonth + offset)}`);
};

export const rangeOfPeriod = (year: number, periodKey: string): VatEngineRange => {
    const keys = monthKeysOfPeriod(year, periodKey);
    const parsed = parsePeriodKey(periodKey);
    return {
        start: monthStart(keys[0]),
        end: monthEnd(keys[keys.length - 1]),
        year,
        periodKey,
        periodType: parsed.type,
    };
};

/** Monoton wachsender Index des Voranmeldungszeitraums, in dem der Monat liegt (für „früherer VAZ"-Vergleiche in R5). */
export const vazIndex = (key: string, periodType: VatPeriodType): number => {
    const {year, month} = partsOrThrow(key);
    return periodType === 'MONAT' ? year * 12 + (month - 1) : year * 4 + Math.floor((month - 1) / 3);
};

export interface LeistungsmonatResult {
    monthKey: string;
    monthSource: 'SOLL_MONTH' | 'TEXT' | 'DATE';
    highDayFallback: boolean;
}

const isAbrechnungsArt = (art: BookingLite['art']): boolean => art === 'BETRIEBSKOSTEN' || art === 'GUTHABEN';

export const resolveLeistungsmonat = (
    booking: Pick<BookingLite, 'sollMonth' | 'text' | 'date' | 'art'>
): LeistungsmonatResult | null => {
    if (isAbrechnungsArt(booking.art)) {
        return booking.date ? {monthKey: monthKey(booking.date), monthSource: 'DATE', highDayFallback: false} : null;
    }
    if (booking.sollMonth && MONTH_KEY_PATTERN.test(booking.sollMonth)) {
        return {monthKey: booking.sollMonth, monthSource: 'SOLL_MONTH', highDayFallback: false};
    }
    const token = TEXT_MONTH_TOKEN.exec(booking.text || '');
    if (token) {
        return {monthKey: `${token[2]}-${token[1]}`, monthSource: 'TEXT', highDayFallback: false};
    }
    if (booking.date) {
        return {monthKey: monthKey(booking.date), monthSource: 'DATE', highDayFallback: booking.date.getDate() >= 29};
    }
    return null;
};
